'use client';

/**
 * CcFunctionsSection - Accordion-style expandable function list
 *
 * Renders a list of high-CC functions with chevron expand to show
 * inline detail (suggestion, CC value, severity, location).
 *
 * Schema section type: cc_functions
 * Receives the `functions` array from cc_module properties.
 *
 * @layer presentation
 * @arch_component_type section
 * @arch_domain system_obeya
 */

import { useState } from 'react';
import { ChevronDown, ChevronRight, FileCode } from 'lucide-react';
import { SectionHeader } from '../shared';
import { cn } from '@/lib/utils/cn';

const SEVERITY_BADGES: Record<string, string> = {
  critical: 'bg-destructive/20 text-destructive',
  high: 'bg-warning/20 text-warning',
  moderate: 'bg-primary/20 text-primary',
};

interface CcFunctionRecord {
  function_name?: unknown;
  name?: unknown;
  cc_value?: unknown;
  severity?: unknown;
  location?: unknown;
  file_stem?: unknown;
  suggestion?: unknown;
}

function InlineFunctionDetail({ f }: { f: CcFunctionRecord }) {
  const severity = String(f.severity ?? 'moderate');
  const badge = SEVERITY_BADGES[severity] ?? SEVERITY_BADGES.moderate;
  const suggestion = f.suggestion as string | undefined;
  const location = f.location as string | undefined;
  const fileStem = f.file_stem as string | undefined;
  const cc = f.cc_value as number | undefined;

  return (
    <div className="pl-6 pr-2 pb-2 space-y-2">
      <div className="flex items-center gap-3 text-xs">
        <span className="font-mono">CC: <span className="font-bold">{cc ?? '-'}</span></span>
        <span className={cn('px-1.5 py-0.5 rounded capitalize', badge)}>{severity}</span>
      </div>

      {suggestion && (
        <div className="p-2 rounded bg-muted/30 border border-border">
          <p className="text-xs text-foreground whitespace-pre-wrap">{suggestion}</p>
        </div>
      )}

      {location && (
        <div className="flex items-center gap-1.5 text-[10px] text-muted-foreground">
          <FileCode className="h-3 w-3 flex-shrink-0" />
          <code className="font-mono break-all">{location}</code>
        </div>
      )}
      {fileStem && !location && (
        <div className="flex items-center gap-1.5 text-[10px] text-muted-foreground">
          <FileCode className="h-3 w-3 flex-shrink-0" />
          <span>{fileStem}</span>
        </div>
      )}
    </div>
  );
}

interface CcFunctionsSectionProps {
  functions: CcFunctionRecord[];
  label?: string | null;
}

export function CcFunctionsSection({ functions, label }: CcFunctionsSectionProps) {
  const [expandedFn, setExpandedFn] = useState<string | null>(null);

  if (!functions || functions.length === 0) {
    return null;
  }

  return (
    <div>
      <SectionHeader title={label ?? `High CC Functions (${functions.length})`} />
      <div className="space-y-1">
        {functions.map((f, i) => {
          const fnName = String(f.function_name ?? f.name ?? '');
          const cc = f.cc_value as number | undefined;
          const sev = String(f.severity ?? 'moderate');
          const badge = SEVERITY_BADGES[sev] ?? SEVERITY_BADGES.moderate;
          const isExpanded = expandedFn === fnName;

          return (
            <div
              key={i}
              className={cn(
                'rounded border border-border transition-colors',
                isExpanded ? 'bg-accent/20' : 'hover:bg-accent/30',
              )}
            >
              <div
                className="flex items-center gap-2 p-2 cursor-pointer"
                onClick={() => setExpandedFn(isExpanded ? null : fnName)}
              >
                {isExpanded
                  ? <ChevronDown className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
                  : <ChevronRight className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />}
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-mono font-medium truncate">{fnName}</p>
                </div>
                <span className={cn('px-1.5 py-0.5 rounded text-[10px] font-mono', badge)}>
                  {cc ?? '-'}
                </span>
              </div>
              {isExpanded && <InlineFunctionDetail f={f} />}
            </div>
          );
        })}
      </div>
    </div>
  );
}
